import { Conversation, ConversationContent, ConversationScrollButton } from '@/ai-elements/conversation';
import React, { memo, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import '../../styles/animations.css';
import type { UIMessage } from '../../types/mastraTypes';
import { useConversationScrollDetection } from '../../hooks/useConversationScrollDetection';
import ConciergeWelcome from '../ConciergeWelcome';
import { ConversationSkeleton } from '../ConversationSkeleton';
import { MessageListV2 } from '../MessageListV2';
import { ChatLiveAnnouncer } from '../ChatLiveAnnouncer';
import { ConciergeSidebar } from '../ConciergeSidebar';
import { ConciergeHomeMobileNav } from '../ConciergeHomeMobileNav';
import {
  ConciergePanelWidget,
  useWidgetConfig,
  useResolveColor,
  customColor,
  PANEL_CHAT_INPUT_PRESETS,
  CHAT_INPUT_PRESETS,
} from '@brandclub/bc-widgets';
import type { ConciergePanelWidgetState } from '@brandclub/bc-widgets';
import { useIsMobile } from './useIsMobile';
import { ConciergeInputArea } from './ConciergeInputArea';
import type { ConciergeChatAreaV2Props } from './types';
import { useLiveTakeoverState } from '../../hooks/useLiveTakeoverState';
import { useConciergeSupportEvents } from '../../hooks/useConciergeSupportEvents';
import { SupportThemeProvider } from '../SupportThemeProvider';
import { useConciergeContext } from '../../ConciergeContextProvider';
import { endLiveAgent } from '../../api';
import { getHeadersWithUniqueVisitorId } from '../../../Auth';

const ConciergeChatAreaV2Component: React.FC<ConciergeChatAreaV2Props> = ({
  appName,
  conciergeName,
  brandId,
  conversationId,
  isEmbedded = false,
  isOnChatWidget = false,
  apiBaseUrl,
  userFirstName,
  pageContext,
  conversationHistory,
  followUpQuestions,
  latestFollowUps,
  isLoadingFollowUps,
  streamingDevInfo,
  isActivelyStreaming,
  transientStatus,
  streamingAIResponse,
  onStop,
  isLoadingConversationQuery,
  isPendingAssistantResponse,
  isPollingTimedOut,
  onRetry,
  onRetryLastMessage,
  streamErrorMessage,
  onStreamErrorRetry,
  onLiveTakeoverActive,
  onTakeoverConversationChange,
  handleSearch,
  onUpdateFeedback,
  onVariantSelect,
  onTrackAccessPoint,
  onHomeClick,
  isSidebarExpanded = true,
  brandLogoUrl,
  searchInputValue,
  setSearchInputValue,
  mobileSidebarProps,
  isSharedView,
  sharedByUserName,
  sourceConversationId,
  sourceCheckpointIndex,
  onOpenShareDialog,
  onShareClick,
  viewMode,
  onSupportEventsReconnect,
}) => {
  const pdpStacklineSku = pageContext?.pageType === 'pdp' ? pageContext.stacklineSku : null;

  const isMobile = useIsMobile();
  const { commerce } = useConciergeContext();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery && !isPendingAssistantResponse && !conversationId;

  // The composable ConciergeHomeLayoutWidget (via ConciergeWelcome) now handles
  // all home screen variations via bc-widgets theme presets. The old isEmbedded-based
  // split between image carousel and text suggestions is no longer needed.
  const hasImageCarousel = false;

  // Check if the last assistant message is a UIMessage with active parts (AI SDK path).
  // When parts are present, the ConciergeMessagePart handles loading states
  // (tool-call skeletons, text streaming) — no separate typing indicator needed.
  const lastMsg = conversationHistory[conversationHistory.length - 1];
  const hasActivePartStreaming =
    lastMsg?.role === 'assistant' &&
    'parts' in lastMsg &&
    Array.isArray((lastMsg as UIMessage).parts) &&
    (lastMsg as UIMessage).parts.length > 0;

  // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
  // AND no parts-based rendering active (AI SDK path shows its own loading states)
  const showTypingIndicator = isActivelyStreaming && !streamingAIResponse && !hasActivePartStreaming;

  // Live support events arriving outside the per-turn LLM stream (human
  // replies, takeover state transitions, ticket-status updates). The hook
  // opens a long-lived SSE on mount and dispatches events into liveParts.
  // Declared early so downstream useCallbacks (renderContent) can reference
  // the synthesized messages without TDZ errors.
  const liveSupportParts = useConciergeSupportEvents(conversationId, apiBaseUrl, onSupportEventsReconnect);

  // Synthesize chat-list messages for human replies arriving via the live
  // events SSE. These are also persisted to Mastra by the webhook receiver,
  // so they survive a reload — but during an active session, only the live
  // SSE has them. Each event becomes its own one-part assistant message,
  // ANCHORED at the position in conversationHistory where it first arrived.
  // Without anchoring, replies would always appear at the bottom of the
  // chat, even ones that arrived chronologically before subsequent user
  // messages or AI responses (visual inversion).
  const liveReplyAnchorsRef = useRef<Map<string, number>>(new Map());

  // Scroll container that wraps the transcript. `ChatLiveAnnouncer` reads the
  // rendered text of the last assistant message out of it once a reply lands.
  const transcriptScopeRef = useRef<HTMLDivElement>(null);

  const messagesWithLiveReplies = useMemo<UIMessage[]>(() => {
    // Build the set of human-reply timestamps already present in
    // conversationHistory (i.e. delivered by Mastra on a normal load OR via
    // a refetch after an SSE reconnect). When a live `data-human-reply`
    // event matches one of these, it has already been persisted server-side
    // and we must NOT synthesize a duplicate UIMessage. Match is by
    // `postedAt` because the persisted message and the live event share
    // Intercom's timestamp but have different `id`s by construction.
    const persistedHumanReplyTimestamps = new Set<string>();
    for (const msg of conversationHistory) {
      const parts = (msg as UIMessage).parts;
      if (!Array.isArray(parts)) continue;
      for (const p of parts as any[]) {
        if (p?.type === 'data-human-reply' && typeof p?.data?.postedAt === 'string') {
          persistedHumanReplyTimestamps.add(p.data.postedAt);
        }
      }
    }

    const liveReplies: { anchor: number; order: number; message: UIMessage }[] = [];
    for (let i = 0; i < liveSupportParts.length; i++) {
      const part = liveSupportParts[i] as any;
      if (part?.type !== 'data-human-reply') continue;
      const postedAt: string | undefined = part?.data?.postedAt;
      // Skip if we already have the persisted version in history. Anchors
      // for skipped entries stay in the ref harmlessly; they're cleared on
      // unmount/reload.
      if (postedAt && persistedHumanReplyTimestamps.has(postedAt)) continue;
      const stableId = postedAt ?? `idx-${i}`;
      if (!liveReplyAnchorsRef.current.has(stableId)) {
        // Anchor at the current end of conversationHistory at first sighting
        liveReplyAnchorsRef.current.set(stableId, conversationHistory.length);
      }
      const anchor = liveReplyAnchorsRef.current.get(stableId)!;
      liveReplies.push({
        anchor,
        order: i,
        message: {
          id: `live-human-reply-${stableId}`,
          role: 'assistant',
          parts: [part],
        } as UIMessage,
      });
    }

    // Sort by anchor ASC so each reply lands at its captured position;
    // ties (same anchor) preserve arrival order.
    liveReplies.sort((a, b) => (a.anchor - b.anchor) || (a.order - b.order));

    if (liveReplies.length === 0) return conversationHistory as UIMessage[];

    // Merge: walk conversationHistory and splice in live replies at their
    // anchor positions. anchor === N means "insert before history[N]"
    // (i.e. between history[N-1] and history[N]).
    const result: UIMessage[] = [];
    let liveIdx = 0;
    for (let i = 0; i <= conversationHistory.length; i++) {
      while (liveIdx < liveReplies.length && liveReplies[liveIdx].anchor === i) {
        result.push(liveReplies[liveIdx].message);
        liveIdx++;
      }
      if (i < conversationHistory.length) {
        result.push(conversationHistory[i] as UIMessage);
      }
    }
    // Catch any with anchors past the end (shouldn't happen but be safe)
    while (liveIdx < liveReplies.length) {
      result.push(liveReplies[liveIdx].message);
      liveIdx++;
    }
    return result;
  }, [conversationHistory, liveSupportParts]);

  const scrollDetectionParams = useMemo(
    () => ({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue: '',
    }),
    [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome],
  );
  const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);

  const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);

  const handleWelcomeSearch = useCallback(
    (searchTerm: string) => {
      onTrackAccessPoint?.('quick_prompts', searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint],
  );

  const handleFollowUpSearch = useCallback(
    (searchTerm: string) => {
      onTrackAccessPoint?.('follow_up_question', searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint],
  );

  const welcomeProps = useMemo(
    () => ({
      onSearch: handleWelcomeSearch,
      brandId: brandId ? parseInt(brandId) : undefined,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      brandName: appName,
      conciergeName,
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile,
    }),
    [
      handleWelcomeSearch,
      brandId,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      appName,
      conciergeName,
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile,
    ],
  );

  const SimpleWelcomeHeader = useMemo(
    () => (
      <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center">
        <p className="tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase tw:text-primary">
          CONCIERGE
        </p>
        <h1 className="tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4 tw:text-primary">
          Let us help you find what you&apos;ve been looking for
          {userFirstName ? `, ${userFirstName}` : ''}.
        </h1>
      </div>
    ),
    [userFirstName],
  );

  // Most recent delivery event observed for the latest user message during
  // the current session. Bridges the gap between the per-turn stream ending
  // (which sets `isActivelyStreaming=false` and would otherwise hide the
  // "Delivering…" pill) and the persisted user message reaching the FE
  // through a Mastra refetch (carrying the data-user-message-mirrored part).
  // `handleHumanTakeoverTurn` publishes the event to `SupportEventBus`, so
  // the long-lived events SSE delivers it within the same tick.
  const latestUserDelivery = useMemo<'delivered' | 'failed' | undefined>(() => {
    let latest: 'delivered' | 'failed' | undefined;
    for (const part of liveSupportParts) {
      if (part.type === 'data-user-message-mirrored') latest = 'delivered';
      else if (part.type === 'data-user-message-delivery-failed') latest = 'failed';
    }
    return latest;
  }, [liveSupportParts]);

  // Live takeover state — computed across all parts of the conversation so it
  // survives mid-stream toggles and reloads. Persisted parts come from
  // conversationHistory; new async events come from liveSupportParts.
  // Declared before `conversationContent` so the JSX below can read
  // `takeover.active` without a TDZ.
  const allParts = useMemo(() => {
    const acc: any[] = [];
    for (const msg of conversationHistory) {
      if (msg && 'parts' in msg && Array.isArray((msg as UIMessage).parts)) {
        acc.push(...((msg as UIMessage).parts as any[]));
      }
    }
    acc.push(...liveSupportParts);
    return acc;
  }, [conversationHistory, liveSupportParts]);
  const takeover = useLiveTakeoverState(allParts);

  // Optimistic "user just clicked Return to Concierge" override. The backend
  // closes the Intercom conversation and publishes `data-live-takeover-ended`
  // over SSE, but that round-trip is ~hundreds of ms. Without this flag the
  // banner would keep its green pulsing dot until SSE catches up. Reset on
  // conversation change OR when a NEW takeover starts (so a future support
  // session re-arms the banner correctly).
  const [endingLocally, setEndingLocally] = useState(false);
  useEffect(() => {
    setEndingLocally(false);
  }, [conversationId]);
  const lastSeenStartedAtRef = useRef<string | undefined>(takeover.startedAt);
  useEffect(() => {
    if (takeover.startedAt && takeover.startedAt !== lastSeenStartedAtRef.current) {
      lastSeenStartedAtRef.current = takeover.startedAt;
      setEndingLocally(false);
    }
  }, [takeover.startedAt]);
  const effectiveTakeoverActive = takeover.active && !endingLocally;

  // Once we know a human teammate is live-handling this thread, tell the
  // container to clear any "waiting for assistant response" polling state.
  // After a reload during takeover the last persisted message is the user's,
  // so the conversation loader optimistically starts polling the AI; the
  // SSE-derived takeover state is the signal that this is a false alarm.
  useEffect(() => {
    if (effectiveTakeoverActive && onLiveTakeoverActive) {
      onLiveTakeoverActive();
    }
  }, [effectiveTakeoverActive, onLiveTakeoverActive]);

  // Publish takeover-state transitions for the current conversation up to the
  // container so the sidebar list can render a small "in takeover" indicator
  // next to the active row. Re-fires on conversation switches and on flips.
  useEffect(() => {
    if (!onTakeoverConversationChange) return;
    onTakeoverConversationChange(effectiveTakeoverActive && conversationId ? conversationId : null);
  }, [effectiveTakeoverActive, conversationId, onTakeoverConversationChange]);

  const conversationContent = useMemo(() => {
    // Show the skeleton only when there's nothing already on screen (fresh load
    // or thread switch, both of which clear messages first). When messages are
    // already present — e.g. the optimistically-seeded voice turns after a call
    // ends — keep them visible and let the background refetch swap them in
    // place, so the overlay->chat handoff doesn't flash a skeleton/white screen.
    if (isLoadingConversationQuery && conversationHistory.length === 0) {
      return <ConversationSkeleton />;
    }
    if (showWelcome) {
      if (hasImageCarousel) {
        return SimpleWelcomeHeader;
      }
      return <ConciergeWelcome {...welcomeProps} />;
    }

    return (
      <MessageListV2
        conversationId={conversationId}
        messages={messagesWithLiveReplies}
        // Suppress the AI "thinking" dots during live takeover — the user is
        // chatting with a person, not waiting on the bot. The "Delivering…"
        // status row under the user's own bubble is the right cue here.
        showTypingIndicator={showTypingIndicator && !effectiveTakeoverActive}
        streamingAIResponse={streamingAIResponse}
        isActivelyStreaming={isActivelyStreaming}
        transientStatus={transientStatus}
        streamingDevInfo={streamingDevInfo}
        followUpQuestions={followUpQuestions}
        latestFollowUps={latestFollowUps}
        isLoadingFollowUps={isLoadingFollowUps}
        onSearch={handleFollowUpSearch}
        onUpdateFeedback={onUpdateFeedback}
        onVariantSelect={onVariantSelect}
        bottomRef={bottomRef as unknown as React.RefObject<HTMLDivElement>}
        apiBaseUrl={apiBaseUrl}
        isPendingAssistantResponse={isPendingAssistantResponse}
        isPollingTimedOut={isPollingTimedOut}
        onRetry={onRetry}
        onRetryLastMessage={onRetryLastMessage}
        streamErrorMessage={streamErrorMessage}
        onStreamErrorRetry={onStreamErrorRetry}
        liveTakeoverActive={effectiveTakeoverActive}
        latestUserDelivery={latestUserDelivery}
        isSharedView={isSharedView}
        sharedByUserName={sharedByUserName}
        sourceConversationId={sourceConversationId}
        sourceCheckpointIndex={sourceCheckpointIndex}
        pdpStacklineSku={pdpStacklineSku}
        isOnChatWidget={isOnChatWidget}
        brandLogoUrl={brandLogoUrl}
        onOpenShareDialog={onOpenShareDialog}
        viewMode={viewMode}
      />
    );
  }, [
    isLoadingConversationQuery,
    showWelcome,
    hasImageCarousel,
    SimpleWelcomeHeader,
    welcomeProps,
    conversationId,
    conversationHistory,
    messagesWithLiveReplies,
    showTypingIndicator,
    streamingAIResponse,
    isActivelyStreaming,
    transientStatus,
    streamingDevInfo,
    followUpQuestions,
    latestFollowUps,
    isLoadingFollowUps,
    handleFollowUpSearch,
    onUpdateFeedback,
    onVariantSelect,
    bottomRef,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    onRetryLastMessage,
    streamErrorMessage,
    onStreamErrorRetry,
    effectiveTakeoverActive,
    latestUserDelivery,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    onOpenShareDialog,
  ]);

  const directPanelState = useWidgetConfig<ConciergePanelWidgetState>('concierge-panel', {
    preset: 'vertical-expanded',
    mainBgColor: customColor('transparent'),
    pillLayout: 'vertical',
    pillShowIcon: false,
    pillBorderRadius: 100,
    pillBorderColor: customColor('#F3F3F3'),
    pillBorderWidth: 1,
    pillShadowEnabled: true,
    pillBgColor: customColor('#ffffff'),
    pillTextColor: customColor('#181d56'),
    pillShadow: '0px 4px 16px 0 rgba(0, 0, 0, 0.05)',
    pillContainerBgColor: customColor('transparent'),
    pillContainerBorderRadius: 12,
    inputBorderColor: customColor('#DCDDDC'),
    inputBorderWidth: 1,
    inputBorderRadius: 20,
    inputMinHeight: 116,
    inputShadowEnabled: false,
    inputShadow: '0px 4px 16px 0 rgba(0, 0, 0, 0.05)',
  });
  const directResolveColor = useResolveColor();

  const handleEndLiveAgent = useCallback(async () => {
    if (!conversationId) return;
    setEndingLocally(true);
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      await endLiveAgent({ conversationId, headersToInject: headers, apiBaseUrl });
    } catch (err) {
      console.warn('Failed to end live agent', err);
      setEndingLocally(false);
    }
  }, [conversationId, apiBaseUrl]);

  const renderContent = (panelState: ConciergePanelWidgetState, resolveColor: typeof directResolveColor) => {
    const bgColor = isOnChatWidget ? resolveColor(panelState.mainBgColor) : undefined;
    return (
      <SupportThemeProvider>
      <div
        className="chat-container tw:relative tw:flex tw:flex-1 tw:flex-col"
        style={{
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
          ...(bgColor && bgColor !== 'transparent' ? { backgroundColor: bgColor } : {}),
        }}
      >
        {/* Mobile sidebar overlay -- always mounted for smooth transitions */}
        {viewMode !== 'read-only' && isMobile && mobileSidebarProps && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              pointerEvents: isMobileSidebarOpen ? 'auto' : 'none',
            }}
          >
            {/* Backdrop with fade transition */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.3)',
                opacity: isMobileSidebarOpen ? 1 : 0,
                transition: 'opacity 0.25s ease-in-out',
              }}
              onClick={() => setIsMobileSidebarOpen(false)}
            />
            {/* Sidebar panel with slide transition */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                transform: isMobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.25s ease-in-out',
              }}
            >
              <ConciergeSidebar
                {...mobileSidebarProps}
                onConversationSelect={(id, mode) => {
                  mobileSidebarProps.onConversationSelect?.(id, mode);
                  setIsMobileSidebarOpen(false);
                }}
                onComposeClick={() => {
                  mobileSidebarProps.onComposeClick?.();
                  setIsMobileSidebarOpen(false);
                }}
                onChatsClick={() => {
                  mobileSidebarProps.onChatsClick?.();
                  setIsMobileSidebarOpen(false);
                }}
                // Sharing opens a dialog over the page; leaving the sidebar
                // overlay standing behind it means dismissing the dialog drops
                // the user back into the drawer they had already left.
                onShareChat={(conversationId) => {
                  mobileSidebarProps.onShareChat?.(conversationId);
                  setIsMobileSidebarOpen(false);
                }}
                isExpanded={true}
                onToggle={() => setIsMobileSidebarOpen(false)}
                mobileOverlay={true}
              />
            </div>
          </div>
        )}

        {/* Persistent mobile nav header -- visible on mobile for both welcome and conversation */}
        {viewMode !== 'read-only' && isMobile && mobileSidebarProps && (
          <ConciergeHomeMobileNav
            brandLogoUrl={brandLogoUrl}
            onMenuClick={() => setIsMobileSidebarOpen(true)}
            // Inside a conversation the X used to leave the Concierge entirely
            // (host home, which on a brand storefront lands on the account
            // page) — a dismissal the user never asked for. Closing a chat
            // should return to the Concierge home; only from the home itself
            // does it exit. Desktop has no X at all, so this also brings the
            // two surfaces closer together.
            onCloseClick={showWelcome ? onHomeClick : mobileSidebarProps.onComposeClick ?? onHomeClick}
            closeLabel={showWelcome ? 'Close Concierge' : 'Close conversation'}
            onShareClick={onShareClick}
            showBag={!!commerce}
          />
        )}

        {/* Embedded welcome */}
        {isEmbedded && showWelcome ? (
          <>
            <div
              className="concierge-conversation-scroll"
              style={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {conversationContent}
              </div>
            </div>
            {viewMode !== 'read-only' && (
              <ConciergeInputArea
                searchInputValue={searchInputValue}
                setSearchInputValue={setSearchInputValue}
                handleSearch={handleSearch}
                isActivelyStreaming={isActivelyStreaming}
                conversationHistoryLength={conversationHistory.length}
                onTrackAccessPoint={onTrackAccessPoint}
                onStop={onStop}
                widgetId="concierge-panel-chat-input"
                displayName="Chat Input (Panel Home)"
                presets={PANEL_CHAT_INPUT_PRESETS}
                forceMobile
                // The `expanded` home input is a 116px-tall two-row box: placeholder
                // on one line, icons on the next. On a phone with the keyboard up
                // that is most of what is left of the sheet, so mobile gets the same
                // single-line `compact` pill the CONVERSATION view already uses
                // below — one preset for the whole mobile bubble instead of a tall
                // one that only appears before the first message. Desktop, where the
                // panel is roomy, keeps the expanded composer.
                initialState={
                  isMobile
                    ? { preset: 'compact', ...PANEL_CHAT_INPUT_PRESETS.compact }
                    : { preset: 'expanded', ...PANEL_CHAT_INPUT_PRESETS.expanded }
                }
                liveTakeoverActive={effectiveTakeoverActive}
                adminName={takeover.adminName}
                adminAvatarUrl={takeover.adminAvatarUrl}
                onEndLiveAgent={handleEndLiveAgent}
              />
            )}
          </>
        ) : (
          <>
            {/* Non-embedded or conversation view */}
            <div
              ref={transcriptScopeRef}
              className="non-embedded-container concierge-conversation-scroll"
              style={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
              }}
            >
              {/* Sits OUTSIDE `Conversation` on purpose: the transcript is
                  `aria-live="off"` and this is the one place a finished reply
                  is announced. */}
              <ChatLiveAnnouncer isActivelyStreaming={isActivelyStreaming} scopeRef={transcriptScopeRef} />
              {showWelcome ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {conversationContent}
                </div>
              ) : (
                <Conversation className="conversation-container">
                  <ConversationContent className="tw:flex tw:items-center tw:justify-center">
                    {conversationContent}
                  </ConversationContent>
                  {/* Scroll-to-bottom affordance is desktop-only; on mobile a
                      swipe/scroll gesture is the natural way to reach bottom
                      and the floating button tends to overlap content. */}
                  {!isMobile && <ConversationScrollButton />}
                </Conversation>
              )}
            </div>

            {/* Input: always visible when embedded, hidden on welcome in full-page mode */}
            {viewMode !== 'read-only' && (isEmbedded || !showWelcome) && (
              <ConciergeInputArea
                searchInputValue={searchInputValue}
                setSearchInputValue={setSearchInputValue}
                handleSearch={handleSearch}
                isActivelyStreaming={isActivelyStreaming}
                conversationHistoryLength={conversationHistory.length}
                onTrackAccessPoint={onTrackAccessPoint}
                onStop={onStop}
                widgetId={isEmbedded ? 'concierge-panel-conversation-chat-input' : 'concierge-conversation-chat-input'}
                displayName={isEmbedded ? 'Chat Input (Panel Conversation)' : 'Chat Input (Conversation)'}
                presets={isEmbedded ? PANEL_CHAT_INPUT_PRESETS : undefined}
                forceMobile={isEmbedded}
                initialState={
                  isEmbedded
                    ? { preset: 'compact', ...PANEL_CHAT_INPUT_PRESETS.compact }
                    : { preset: 'compact', ...CHAT_INPUT_PRESETS.compact }
                }
                liveTakeoverActive={effectiveTakeoverActive}
                adminName={takeover.adminName}
                adminAvatarUrl={takeover.adminAvatarUrl}
                onEndLiveAgent={handleEndLiveAgent}
              />
            )}
          </>
        )}
      </div>
      </SupportThemeProvider>
    );
  };

  if (isEmbedded) {
    return (
      <ConciergePanelWidget
        widgetId="concierge-panel"
        displayName="Concierge Panel"
        suggestionListWidgetId="concierge-suggestion-list"
        chatInputWidgetId="concierge-panel-chat-input"
        suppressChildEdits={false}
      >
        {(panelState, resolveColor) => renderContent(panelState, resolveColor)}
      </ConciergePanelWidget>
    );
  }

  return renderContent(directPanelState, directResolveColor);
};

// Memoize the chat area - input is separated so it can re-render without affecting conversation
export const ConciergeChatAreaV2 = memo(ConciergeChatAreaV2Component);

export type { ConciergeChatAreaV2Props } from './types';
